import * as moment from "moment-timezone";
import type { ReporteChequeo } from "../models/ReporteChequeo.ts";
import type { BranchReportMeta } from "./payrollBranchMeta.ts";
import { parseTimeToMinutes } from "./payrollBranchMeta.ts";
import {
  effectiveNightMetaForPartner,
  isPartnerNocturnoShift,
  partnerShiftFromIncidentSchedule,
} from "./nightScheduleEligibility.ts";
import {
  getReportDateKey,
  isStandardShiftDaytimePunch,
  operationalDayKeyForReportCheck,
} from "./operationalDayReporteChequeo.ts";
import {
  buildVisitRowsForPartnerBranch,
  isSameCalendarDayDayShiftOrdered,
  isSameCalendarDayExtendedDayShift,
  isSameCalendarDayMatutinoShift,
} from "./visitPairingReporteChequeo.ts";

const MORNING_MAX_MINUTES = 10 * 60;
const EVENING_MIN_MINUTES = 12 * 60;
/** Salida a medianoche en cuadro vespertino (00:00), no es matutino invertido. */
const MIDNIGHT_END_MAX_MINUTES = 60;

function looksLikeMidnightScheduleEnd(salidaMin: number): boolean {
  return salidaMin === 0 || (salidaMin > 0 && salidaMin <= MIDNIGHT_END_MAX_MINUTES);
}

function splitScheduleTime(value?: string | null, part: "start" | "end" = "start"): string {
  if (!value) return "";
  const parts = String(value).split(" - ").map((p) => p.trim());
  if (part === "start") {
    return parts[0] || "";
  }
  return parts[1] || parts[0] || "";
}

/**
 * Horario programado para UI (TableChequeo / incidencias / nómina).
 * Con horario nocturno, si en BD quedó invertido (05:38 – 21:08), se muestra 21:08 – 05:38.
 */
export function resolveScheduledTimesForDisplay(
  startTime?: string | null,
  endTime?: string | null,
  nightScheduleEnabled = false
): { entrada: string; salida: string } {
  const entrada = splitScheduleTime(startTime, "start");
  const salida = splitScheduleTime(endTime, "end");

  if (!entrada || !salida) {
    return { entrada, salida };
  }

  const entradaMin = parseTimeToMinutes(entrada);
  const salidaMin = parseTimeToMinutes(salida);

  const looksLikeMatutinoSchedule =
    entradaMin < MORNING_MAX_MINUTES &&
    salidaMin >= 11 * 60 &&
    salidaMin <= 15 * 60 &&
    salidaMin > entradaMin;

  /** BD guardó matutino al revés: tarde en startTime, madrugada en endTime (incidencias no programadas). */
  const looksLikeStoredMatutinoInverted =
    !looksLikeMatutinoSchedule &&
    !looksLikeMidnightScheduleEnd(salidaMin) &&
    entradaMin >= EVENING_MIN_MINUTES &&
    salidaMin >= 4 * 60 &&
    salidaMin < MORNING_MAX_MINUTES &&
    salidaMin < entradaMin;

  if (looksLikeStoredMatutinoInverted) {
    return { entrada: salida, salida: entrada };
  }

  if (!nightScheduleEnabled) {
    return { entrada, salida };
  }

  const looksInverted =
    !looksLikeMatutinoSchedule &&
    ((entradaMin < MORNING_MAX_MINUTES && salidaMin >= EVENING_MIN_MINUTES) ||
      (entradaMin >= EVENING_MIN_MINUTES &&
        salidaMin < MORNING_MAX_MINUTES &&
        salidaMin < entradaMin));

  if (looksInverted) {
    return { entrada: salida, salida: entrada };
  }

  return { entrada, salida };
}

type CheckLike = {
  id?: number;
  checkTime?: string;
  checkDate?: string | Date;
} | null | undefined;

type IncidentScheduleLike = {
  id?: number;
  exactDate?: string | Date;
  startTime?: string | null;
  endTime?: string | null;
  positionX?: number;
  positionY?: number;
  unscheduled?: boolean;
  partner?: { id?: number; candidate?: { shift?: { name?: string } } };
  branch?: { id?: number };
  branchId?: number;
  shift?: string;
  assistanceCheck?: CheckLike;
  assistanceCheckout?: CheckLike;
  checks?: CheckLike[];
};

type IncidentRecordLike = {
  checkIn?: CheckLike;
  checkOut?: CheckLike;
  schedule?: IncidentScheduleLike;
};

/**
 * Alinea checadas reales al turno (entrada antes que salida en sentido laboral).
 */
export function normalizeRealChecksForDisplay(
  checkIn: CheckLike,
  checkOut: CheckLike,
  nightScheduleEnabled = false
): { checkIn: CheckLike; checkOut: CheckLike } {
  if (!nightScheduleEnabled || !checkIn?.checkTime || !checkOut?.checkTime) {
    return { checkIn, checkOut };
  }

  const inMin = parseTimeToMinutes(checkIn.checkTime);
  const outMin = parseTimeToMinutes(checkOut.checkTime);
  const crossDay =
    Boolean(checkIn.checkDate) &&
    Boolean(checkOut.checkDate) &&
    String(checkIn.checkDate) !== String(checkOut.checkDate);

  if (crossDay) {
    if (inMin < MORNING_MAX_MINUTES && outMin >= EVENING_MIN_MINUTES) {
      return { checkIn: checkOut, checkOut: checkIn };
    }
    return { checkIn, checkOut };
  }

  if (inMin > outMin && outMin < MORNING_MAX_MINUTES) {
    return { checkIn: checkOut, checkOut: checkIn };
  }

  if (inMin < MORNING_MAX_MINUTES && outMin >= EVENING_MIN_MINUTES) {
    return { checkIn: checkOut, checkOut: checkIn };
  }

  return { checkIn, checkOut };
}

export type ResolveIncidentRealChecksOptions = {
  branchMeta?: BranchReportMeta;
  shiftName?: string | null;
  /**
   * Jefe directo: si hay colaboradores Nocturno, usar el mayor corte entre
   * sucursales con horario nocturno (solo cuando la sucursal del colaborador
   * también tiene nocturno habilitado).
   */
  maxNightCutoffMinutes?: number;
};

function resolveNightMetaForIncident(
  record: IncidentRecordLike,
  options?: boolean | ResolveIncidentRealChecksOptions,
  legacyCutoffMinutes = 3 * 60
): BranchReportMeta {
  if (typeof options === "boolean") {
    return {
      nightScheduleEnabled: options,
      cutoffMinutes: legacyCutoffMinutes,
    };
  }

  const shiftName =
    options?.shiftName ?? partnerShiftFromIncidentSchedule(record?.schedule);
  const fromBranch = effectiveNightMetaForPartner(
    options?.branchMeta,
    shiftName
  );
  if (!fromBranch.nightScheduleEnabled) {
    return fromBranch;
  }

  const maxCutoff = options?.maxNightCutoffMinutes;
  if (maxCutoff != null && maxCutoff > 0 && isPartnerNocturnoShift(shiftName)) {
    return {
      nightScheduleEnabled: true,
      cutoffMinutes: maxCutoff,
    };
  }

  return fromBranch;
}

function isUnprogrammedIncidentSchedule(
  schedule?: IncidentScheduleLike
): boolean {
  if (!schedule) return false;
  if (
    schedule.unscheduled === true ||
    Number(schedule.positionX) === -1 ||
    Number(schedule.positionY) === -1
  ) {
    return true;
  }
  const { entrada, salida } = resolveScheduledTimesForDisplay(
    schedule.startTime,
    schedule.endTime,
    false
  );
  return !entrada && !salida;
}

/** Primera y última checada del día operativo (mismo criterio que TableChequeo / RowUnschedule). */
function resolveUnprogrammedTableChequeoPair(
  record: IncidentRecordLike
): { checkIn: CheckLike; checkOut: CheckLike } {
  const operationDate =
    formatCheckDateYmd(record?.schedule?.exactDate) ||
    formatCheckDateYmd(record?.checkIn?.checkDate);

  const pool = new Map<string, CheckLike>();
  const add = (check: CheckLike) => {
    if (!check?.checkTime) return;
    const key =
      check.id != null
        ? `id:${check.id}`
        : `${formatCheckDateYmd(check.checkDate)}|${check.checkTime}`;
    pool.set(key, check);
  };

  for (const check of record?.schedule?.checks ?? []) add(check);
  add(record?.checkIn);
  add(record?.checkOut);
  add(record?.schedule?.assistanceCheck);
  add(record?.schedule?.assistanceCheckout);

  const sameDay = [...pool.values()]
    .filter((check): check is NonNullable<CheckLike> => {
      if (!check?.checkTime) return false;
      if (!operationDate) return true;
      return formatCheckDateYmd(check.checkDate) === operationDate;
    })
    .sort((a, b) =>
      String(a.checkTime ?? "").localeCompare(String(b.checkTime ?? ""))
    );

  if (sameDay.length >= 2) {
    return sanitizeIncidentCheckPair(
      sameDay[0],
      sameDay[sameDay.length - 1]
    );
  }
  if (sameDay.length === 1) {
    return sanitizeIncidentCheckPair(sameDay[0], null);
  }
  return { checkIn: null, checkOut: null };
}

function applyCrossDayCheckoutCutoffRule(
  checkIn: CheckLike,
  checkOut: CheckLike | null,
  effectiveMeta: BranchReportMeta
): { checkIn: CheckLike; checkOut: CheckLike } {
  if (!checkIn) {
    return { checkIn: null, checkOut: null };
  }
  if (!checkOut || effectiveMeta.nightScheduleEnabled) {
    return sanitizeIncidentCheckPair(checkIn, checkOut);
  }
  const inDate = formatCheckDateYmd(checkIn.checkDate);
  const outDate = formatCheckDateYmd(checkOut.checkDate);
  const outMin = parseTimeToMinutes(String(checkOut.checkTime || ""));
  if (
    inDate &&
    outDate &&
    inDate !== outDate &&
    outMin >= effectiveMeta.cutoffMinutes
  ) {
    return sanitizeIncidentCheckPair(checkIn, null);
  }
  return sanitizeIncidentCheckPair(checkIn, checkOut);
}

export function resolveIncidentRealChecks(
  record: IncidentRecordLike,
  options?: boolean | ResolveIncidentRealChecksOptions,
  legacyCutoffMinutes = 3 * 60
): { checkIn: CheckLike; checkOut: CheckLike } {
  const effectiveMeta = resolveNightMetaForIncident(
    record,
    options,
    legacyCutoffMinutes
  );
  const branchMeta =
    typeof options === "object" ? options?.branchMeta : undefined;
  const shiftName =
    typeof options === "object"
      ? options?.shiftName
      : partnerShiftFromIncidentSchedule(record?.schedule);

  const incidentStatus = String(
    (record as { status?: string })?.status ?? ""
  ).trim();

  if (incidentStatus === "No tuvo chequeo de salida") {
    const rawIn =
      record?.checkIn ?? record?.schedule?.assistanceCheck ?? null;
    return applyCrossDayCheckoutCutoffRule(rawIn, null, effectiveMeta);
  }

  if (incidentStatus === "No tuvo chequeo de entrada") {
    const rawOut =
      record?.checkOut ?? record?.schedule?.assistanceCheckout ?? null;
    return applyCrossDayCheckoutCutoffRule(null, rawOut, effectiveMeta);
  }

  const isUnprogrammed = isUnprogrammedIncidentSchedule(record?.schedule);

  if (isUnprogrammed) {
    const tablePair = resolveUnprogrammedTableChequeoPair(record);
    if (tablePair.checkIn) {
      return applyCrossDayCheckoutCutoffRule(
        tablePair.checkIn,
        tablePair.checkOut,
        effectiveMeta
      );
    }

    const dayShiftPair = resolveIncidentRealChecksFromSameCalendarDay(
      record,
      effectiveMeta.cutoffMinutes
    );
    if (dayShiftPair.checkIn) {
      return applyCrossDayCheckoutCutoffRule(
        dayShiftPair.checkIn,
        dayShiftPair.checkOut,
        effectiveMeta
      );
    }

    const paired = resolveIncidentRealChecksFromOperationalVisits(
      record,
      branchMeta,
      shiftName ?? undefined,
      effectiveMeta.cutoffMinutes,
      effectiveMeta.nightScheduleEnabled
    );
    if (paired.checkIn) {
      return applyCrossDayCheckoutCutoffRule(
        paired.checkIn,
        paired.checkOut,
        effectiveMeta
      );
    }

    const rawIn =
      record?.checkIn ?? record?.schedule?.assistanceCheck ?? null;
    const rawOut =
      record?.checkOut ?? record?.schedule?.assistanceCheckout ?? null;
    return applyCrossDayCheckoutCutoffRule(rawIn, rawOut, effectiveMeta);
  }

  const apiIn =
    record?.checkIn ?? record?.schedule?.assistanceCheck ?? null;
  const apiOut =
    record?.checkOut ?? record?.schedule?.assistanceCheckout ?? null;
  if (
    apiIn?.checkTime &&
    apiOut?.checkTime &&
    !areSameCheckLike(apiIn, apiOut)
  ) {
    return applyCrossDayCheckoutCutoffRule(apiIn, apiOut, effectiveMeta);
  }

  if (effectiveMeta.nightScheduleEnabled) {
    const paired = resolveIncidentRealChecksFromOperationalVisits(
      record,
      branchMeta,
      shiftName ?? undefined,
      effectiveMeta.cutoffMinutes,
      true
    );
    if (paired.checkIn) {
      return applyCrossDayCheckoutCutoffRule(
        paired.checkIn,
        paired.checkOut,
        effectiveMeta
      );
    }
  } else {
    const paired = resolveIncidentRealChecksFromOperationalVisits(
      record,
      branchMeta,
      shiftName ?? undefined,
      effectiveMeta.cutoffMinutes,
      false
    );
    if (paired.checkIn) {
      return applyCrossDayCheckoutCutoffRule(
        paired.checkIn,
        paired.checkOut,
        effectiveMeta
      );
    }
  }

  const dayShiftPair = resolveIncidentRealChecksFromSameCalendarDay(
    record,
    effectiveMeta.cutoffMinutes
  );
  if (dayShiftPair.checkIn) {
    return applyCrossDayCheckoutCutoffRule(
      dayShiftPair.checkIn,
      dayShiftPair.checkOut,
      effectiveMeta
    );
  }

  const rawIn =
    record?.checkIn ?? record?.schedule?.assistanceCheck ?? null;
  const rawOut =
    record?.checkOut ?? record?.schedule?.assistanceCheckout ?? null;

  const normalized = normalizeRealChecksForDisplay(
    rawIn,
    rawOut,
    effectiveMeta.nightScheduleEnabled
  );
  return applyCrossDayCheckoutCutoffRule(
    normalized.checkIn,
    normalized.checkOut,
    effectiveMeta
  );
}

const MIN_DAYTIME_ENTRY_MINUTES = 10 * 60;

/** Turno diurno en el mismo día calendario (p. ej. 08:06–18:16), como TableReporteChequeo. */
function resolveIncidentRealChecksFromSameCalendarDay(
  record: IncidentRecordLike,
  cutoffMinutes: number
): { checkIn: CheckLike; checkOut: CheckLike } {
  const schedule = record?.schedule;
  const partnerId = Number(schedule?.partner?.id);
  const branchId = Number(schedule?.branchId ?? schedule?.branch?.id);
  const operationDate = formatCheckDateYmd(schedule?.exactDate);
  const rawChecks = Array.isArray(schedule?.checks) ? schedule.checks : [];

  if (!partnerId || !branchId || !operationDate || rawChecks.length < 2) {
    return { checkIn: null, checkOut: null };
  }

  const sameDayChecks = rawChecks
    .filter(
      (check): check is NonNullable<CheckLike> =>
        check != null &&
        formatCheckDateYmd(check.checkDate) === operationDate
    )
    .sort((a, b) =>
      String(a.checkTime ?? "").localeCompare(String(b.checkTime ?? ""))
    );

  const hasAfternoonEntry = sameDayChecks.some((check) =>
    parseTimeToMinutes(String(check.checkTime || "")) >= MIN_DAYTIME_ENTRY_MINUTES
  );
  const filteredSameDayChecks = hasAfternoonEntry
    ? sameDayChecks.filter((check) =>
        isStandardShiftDaytimePunch(String(check.checkTime || ""), cutoffMinutes)
      )
    : sameDayChecks;

  if (filteredSameDayChecks.length < 2) {
    return { checkIn: null, checkOut: null };
  }

  const reportRows = filteredSameDayChecks
    .map((check) =>
      incidentCheckToReporteChequeo(check, partnerId, branchId)
    )
    .filter((row): row is ReporteChequeo => row !== null);

  const isMatutinoDay = isSameCalendarDayMatutinoShift(reportRows);
  const isExtendedDay = isSameCalendarDayExtendedDayShift(reportRows);
  if (
    (!isMatutinoDay &&
      !isExtendedDay &&
      !isSameCalendarDayDayShiftOrdered(reportRows, cutoffMinutes)) ||
    reportRows.length < 2
  ) {
    return { checkIn: null, checkOut: null };
  }

  const checkIn = filteredSameDayChecks[0];
  const checkOut = filteredSameDayChecks[filteredSameDayChecks.length - 1];
  return sanitizeIncidentCheckPair(checkIn ?? null, checkOut ?? null);
}

function formatCheckDateYmd(value: unknown): string {
  if (!value) return "";
  return moment.utc(value).format("YYYY-MM-DD");
}

function areSameCheckLike(a: CheckLike, b: CheckLike): boolean {
  if (!a || !b) return false;
  if (a.id != null && b.id != null && Number(a.id) === Number(b.id)) {
    return true;
  }
  return (
    formatCheckDateYmd(a.checkDate) === formatCheckDateYmd(b.checkDate) &&
    String(a.checkTime || "") === String(b.checkTime || "")
  );
}

function sanitizeIncidentCheckPair(
  checkIn: CheckLike,
  checkOut: CheckLike | null
): { checkIn: CheckLike; checkOut: CheckLike } {
  if (!checkIn) {
    return { checkIn: null, checkOut: null };
  }
  if (areSameCheckLike(checkIn, checkOut)) {
    return { checkIn, checkOut: null };
  }
  return { checkIn, checkOut };
}

function incidentCheckToReporteChequeo(
  check: CheckLike,
  partnerId: number,
  branchId: number,
  shiftName?: string
): ReporteChequeo | null {
  if (!check?.id || !check.checkTime) return null;
  const checkDate = formatCheckDateYmd(check.checkDate);
  if (!checkDate) return null;
  return {
    assistanceCheck_id: Number(check.id),
    assistanceCheck_checkDate: checkDate,
    assistanceCheck_checkTime: String(check.checkTime),
    candidate_shift_name: shiftName,
    partner_id: partnerId,
    branch_id: branchId,
    branch_name: "",
    person_firstName: "",
    person_lastName: "",
    person_secondLastName: "",
    position_name: "",
    assistanceCheck_coords: "",
    assistanceCheck_createdAt: "",
  };
}

/** Mismo pareo que TableReporteChequeo (visitas por día operativo / vespertino estándar). */
function resolveIncidentRealChecksFromOperationalVisits(
  record: IncidentRecordLike,
  branchMeta: BranchReportMeta | undefined,
  shiftName: string | undefined,
  cutoffMinutes: number,
  nightScheduleEnabled: boolean
): { checkIn: CheckLike; checkOut: CheckLike } {
  const schedule = record?.schedule;
  const partnerId = Number(schedule?.partner?.id);
  const branchId = Number(schedule?.branchId ?? schedule?.branch?.id);
  const operationDate = formatCheckDateYmd(schedule?.exactDate);

  const rawChecks = Array.isArray(schedule?.checks) ? schedule.checks : [];
  if (!partnerId || !branchId || !operationDate || rawChecks.length === 0) {
    return { checkIn: null, checkOut: null };
  }

  const baseMeta: BranchReportMeta = effectiveNightMetaForPartner(
    branchMeta,
    shiftName
  );

  const reportChecks = rawChecks
    .map((check) =>
      incidentCheckToReporteChequeo(check, partnerId, branchId, shiftName)
    )
    .filter((row): row is ReporteChequeo => row !== null);

  if (reportChecks.length === 0) {
    return { checkIn: null, checkOut: null };
  }

  const nextCalKey = moment
    .utc(operationDate, "YYYY-MM-DD")
    .add(1, "day")
    .format("YYYY-MM-DD");

  const checkCurrent = reportChecks.filter((row) => {
    const calKey = getReportDateKey(row.assistanceCheck_checkDate);
    if (calKey !== operationDate) {
      return false;
    }
    if (!nightScheduleEnabled) {
      return isStandardShiftDaytimePunch(
        row.assistanceCheck_checkTime,
        cutoffMinutes
      );
    }
    return (
      operationalDayKeyForReportCheck(row, baseMeta, reportChecks) ===
      operationDate
    );
  });
  const checkNext = reportChecks.filter((row) => {
    const calKey = getReportDateKey(row.assistanceCheck_checkDate);
    if (calKey !== nextCalKey) {
      return false;
    }
    if (
      parseTimeToMinutes(row.assistanceCheck_checkTime) >= cutoffMinutes
    ) {
      return false;
    }
    if (!nightScheduleEnabled) {
      return true;
    }
    return (
      operationalDayKeyForReportCheck(row, baseMeta, reportChecks) ===
      operationDate
    );
  });

  const visitRows = buildVisitRowsForPartnerBranch(
    checkCurrent,
    checkNext,
    branchId,
    partnerId,
    checkCurrent,
    checkNext,
    cutoffMinutes,
    nightScheduleEnabled
  );

  if (!visitRows.length) {
    return { checkIn: null, checkOut: null };
  }

  const rawInId = Number(
    record?.checkIn?.id ?? schedule?.assistanceCheck?.id ?? 0
  );
  const rawOutId = Number(
    record?.checkOut?.id ?? schedule?.assistanceCheckout?.id ?? 0
  );

  let chosen = visitRows[0];
  if (rawInId > 0 || rawOutId > 0) {
    const byId = visitRows.find((row) => {
      const inId = Number(row.checkIn?.assistanceCheck_id ?? 0);
      const outId = Number(row.checkOut?.assistanceCheck_id ?? 0);
      return (
        (rawInId > 0 && inId === rawInId) ||
        (rawOutId > 0 && outId === rawOutId) ||
        (rawInId > 0 && outId === rawInId)
      );
    });
    if (byId) chosen = byId;
  } else {
    const withCheckout = visitRows.filter((row) => row.checkOut);
    if (withCheckout.length > 0) {
      chosen =
        withCheckout.find((row) => {
          const t = row.checkIn?.assistanceCheck_checkTime ?? "";
          return parseTimeToMinutes(t) < 12 * 60;
        }) ?? withCheckout[0];
    } else {
      const evening = visitRows.find((row) => {
        const t = row.checkIn?.assistanceCheck_checkTime ?? "";
        return parseTimeToMinutes(t) >= 12 * 60;
      });
      if (evening) chosen = evening;
    }
  }

  const mapBack = (reportCheck?: ReporteChequeo | null): CheckLike => {
    if (!reportCheck) return null;
    return {
      id: reportCheck.assistanceCheck_id,
      checkDate: reportCheck.assistanceCheck_checkDate,
      checkTime: reportCheck.assistanceCheck_checkTime,
    };
  };

  return sanitizeIncidentCheckPair(
    mapBack(chosen.checkIn),
    mapBack(chosen.checkOut ?? null)
  );
}
